<script setup lang="ts">
import ApplicationStepHeader from '@/components/common/application-step-header.vue';
import ProductSearch from '@/components/goods-view/components/product-search.vue';
import ProductResult from '@/components/goods-view/components/product-result.vue';
import {useProductStore} from '@/store/good';
import {storeToRefs} from 'pinia';
import ProductCart from '@/components/goods-view/components/product-cart.vue';
import useCurrentSearch from '@/components/goods-view/searchDataStore';
import {useApplicationStore} from '@/store/application';
import SummaryBanner from '@/components/SummaryBanner.vue';
import { ref, watch, onUnmounted } from 'vue';

const productStore = useProductStore();
const {haveSearched} = useCurrentSearch();
const {totalGoods, isProductStepValid} = storeToRefs(productStore);
const { hasValidation } = storeToRefs(useApplicationStore());
const loading = ref(false);

// iOS-safe body overflow handling
const setBodyOverflow = (hidden: boolean) => {
    const body = document.body;
    if (hidden) {
        // Store current scroll position
        const scrollY = window.scrollY;
        body.style.position = 'fixed';
        body.style.top = `-${scrollY}px`;
        body.style.width = '100%';
        body.style.overflow = 'hidden';
        // Store scroll position for restoration
        body.dataset.scrollY = scrollY.toString();
    } else {
        // Restore scroll position and remove fixed positioning
        const scrollY = parseInt(body.dataset.scrollY || '0');
        body.style.position = '';
        body.style.top = '';
        body.style.width = '';
        body.style.overflow = '';
        delete body.dataset.scrollY;
        // Restore scroll position
        window.scrollTo(0, scrollY);
    }
};

watch(loading, (newValue) => {
    setBodyOverflow(newValue);
});

// Cleanup on component unmount
onUnmounted(() => {
    if (loading.value) {
        setBodyOverflow(false);
    }
});
</script>

<template>
    <div>
        <StfCard :success="isProductStepValid" :error="hasValidation && !isProductStepValid">
            <template #title>
                <ApplicationStepHeader title="Your Products & Services" :help="true" help-title="Your Products & Services">
                    <template #help>
                        <div class="mb-2 stf-text-bold">You need to select the products and/or services you will use your trademark for.</div>
                        <div class="mb-2">Products and services are grouped into different categories/classes.</div>
                        <div class="mb-2">Search for words that describe the products that you sell (eg. clothing), or services that you provide (eg. accounting).</div>
                        <div class="mb-2">The search results will show descriptions that have been approved by the Trademark Office. Simply choose the ones that best reflect your business.</div>
                        <div class="mb-2">Sometimes these might not exactly match how you describe your products/services, but you should be able to find something close.</div>
                        <div class="mb-2 stf-text-bold">How our pricing works</div>
                        <div class="mb-2">One trademark in one class costs $650. Each extra class in the same application costs $500.</div>
                        <div class="mb-2">If you are cost-conscious, choose the class/es that are most important to you. Getting some protection is better than none at all!</div>
                        <div>The Trademark Office doesn't allow classes to be added to an application later but you can submit another application at anytime that covers additional products/services.</div>
                    </template>
                </ApplicationStepHeader>
            </template>
            <template #content>
                <product-search @loading-change="(newValue) => loading = newValue"/>
                <div v-if="loading" class="py-6">
                    <div class="stf-spinner stf-spinner--large m-auto"></div>
                    <div class="has-text-centered mt-4">Searching for results...</div>
                </div>
                <template v-if="haveSearched && !loading">
                    <StfDivider/>
                    <product-result class="my-2"/>
                </template>
                <template v-if="totalGoods > 0">
                    <product-cart/>
                </template>
            </template>
        </StfCard>
        <SummaryBanner />
    </div>
</template>
